import React, { useState, useEffect, useMemo } from "react";
import { getFirestore, collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { Table, TableBody, TableContainer, TableHead, Paper, TextField, Box, TableSortLabel, Tooltip, CircularProgress, Typography } from "@mui/material";
import { StyledTableCell, StyledTableRow } from '../../utils/styled';
import { getPricesOnce } from "../../services/priceService";
import moment from "moment";
import "moment/locale/es";

const firestore = getFirestore();

// Precios: puede ser array (Firestore) u objeto por id
const pricesById = (prices) => {
    if (!prices) return {};
    return Array.isArray(prices) ? prices.reduce((acc, p) => { acc[p.id] = p; return acc; }, {}) : prices;
};

const priceValueServices = (services, id, tipoDia, pricesMap) => {
    const newFilter = services?.filter(valor => valor.id === id) || [];
    const valueExist = newFilter.length > 0;
    const typeValue = valueExist && typeof newFilter[0]?.value === 'boolean';
    let priceService = 0;
    if (valueExist && pricesMap && pricesMap[id]) {
        const priceHogar = tipoDia === 'Semana' ?
            parseInt(pricesMap[id]?.hogar, 10) || 0 :
            parseInt(pricesMap[id]?.feriado_domingo_hogar, 10) || 0;
        priceService = typeValue ? priceHogar : priceHogar * (parseInt(newFilter[0]?.value, 10) || 0);
    }
    return priceService || 0;
};

const calculateOrderTotal = (order, pricesMap) => {
    let total = 0;
    if (order?.services && Array.isArray(order.services)) {
        order.services.forEach(service => {
            total += priceValueServices(order.services, service.id, order?.tipoDia, pricesMap);
        });
    }
    return total;
};

const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '₲ 0';
    const n = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(n)) return '₲ 0';
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
};

const Produccion = () => {
    const [mesSeleccionado, setMesSeleccionado] = useState(moment().format("YYYY-MM"));
    const [ordenes, setOrdenes] = useState(null);
    const [prices, setPrices] = useState([]);
    const [sortBy, setSortBy] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');
    const [asistenciasMes, setAsistenciasMes] = useState({});
    const [feriados, setFeriados] = useState({});

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    useEffect(() => {
        getPricesOnce(setPrices);
    }, []);

    useEffect(() => {
        const loadAllData = async () => {
            setOrdenes(null);
            try {
                const [year, month] = mesSeleccionado.split("-");
                const inicioMes = `${year}${month}01`;
                const finMes = `${year}${month}31`;

                const ordenesRef = collection(firestore, "ordenes");
                const q = query(
                    ordenesRef,
                    where("date", ">=", inicioMes),
                    where("date", "<=", finMes)
                );

                const asistenciasRef = collection(firestore, `asistencias/${year}/${month}`);
                const diasDocRef = doc(firestore, `asistencias/${year}/${month}/dias`);

                const [snapshotOrdenes, snapshotAsistencias, diasDocSnap] = await Promise.all([
                    getDocs(q),
                    getDocs(asistenciasRef),
                    getDoc(diasDocRef)
                ]);

                const dataOrdenes = snapshotOrdenes.docs.map(doc => doc.data());
                setOrdenes(dataOrdenes);

                if (diasDocSnap.exists()) {
                    setFeriados(diasDocSnap.data().feriados || {});
                } else {
                    setFeriados({});
                }

                const mapAsistencias = {};
                snapshotAsistencias.docs.forEach(d => {
                    if (d.id === "dias") return;
                    mapAsistencias[d.id] = {
                        dias_no_asistidos: d.data().dias_no_asistidos || {},
                        dias_backup: d.data().dias_backup || {}
                    };
                });
                setAsistenciasMes(mapAsistencias);

            } catch (err) {
                console.error("Error loading production data:", err);
                setOrdenes([]);
            }
        };

        loadAllData();
    }, [mesSeleccionado]);

    const ordenesPorCuadrilla = useMemo(() => {
        const pricesMap = pricesById(prices);
        let agrupadas = {};
        if (!ordenes) return agrupadas;

        ordenes.forEach(orden => {
            const { idCuadrilla, cuadrilla, usuarioCuadrilla, date, confectionData } = orden;

            let dia;
            if (date) {
                dia = parseInt(date.slice(-2));
            } else if (confectionData) {
                const fechaConfection = moment(confectionData, "DD/MM/YYYY HH:mm");
                if (!fechaConfection.isValid()) return;
                dia = fechaConfection.date();
            } else {
                return;
            }

            if (!agrupadas[idCuadrilla]) {
                agrupadas[idCuadrilla] = { cuadrilla, usuarioCuadrilla, total: 0, facturacionTotal: 0, dias: {} };
            }
            if (!agrupadas[idCuadrilla].dias[dia]) {
                agrupadas[idCuadrilla].dias[dia] = 0;
            }

            agrupadas[idCuadrilla].dias[dia]++;
            agrupadas[idCuadrilla].total++;
            agrupadas[idCuadrilla].facturacionTotal += calculateOrderTotal(orden, pricesMap);
        });

        return Object.keys(agrupadas).sort((a, b) => a - b).reduce((acc, key) => {
            acc[key] = agrupadas[key];
            return acc;
        }, {});
    }, [ordenes, prices]);

    const diasMes = moment(mesSeleccionado, "YYYY-MM").daysInMonth();
    const sortedCuadrillaIds = useMemo(() => {
        const ids = Object.keys(ordenesPorCuadrilla);
        if (!sortBy) return ids.sort((a, b) => Number(a) - Number(b));
        return [...ids].sort((a, b) => {
            const rowA = ordenesPorCuadrilla[a];
            const rowB = ordenesPorCuadrilla[b];
            const diasConProdA = [...Array(diasMes).keys()].filter(i => rowA.dias[i + 1]).length;
            const diasConProdB = [...Array(diasMes).keys()].filter(i => rowB.dias[i + 1]).length;
            let valA, valB;
            if (sortBy === 'facturacionTotal') {
                valA = rowA.facturacionTotal ?? 0;
                valB = rowB.facturacionTotal ?? 0;
            } else {
                valA = diasConProdA > 0 ? (rowA.facturacionTotal ?? 0) / diasConProdA : 0;
                valB = diasConProdB > 0 ? (rowB.facturacionTotal ?? 0) / diasConProdB : 0;
            }
            if (valA === valB) return Number(a) - Number(b);
            const mult = sortOrder === 'asc' ? 1 : -1;
            return mult * (valA - valB);
        });
    }, [ordenesPorCuadrilla, diasMes, sortBy, sortOrder]);

    return (
        <div>
            <h2>Facturación de las Cuadrillas</h2>
            <Box display="flex" justifyContent="center" marginBottom={2}>
                <TextField
                    type="month"
                    value={mesSeleccionado}
                    onChange={(e) => setMesSeleccionado(e.target.value)}
                />
            </Box>
            <TableContainer
                    component={Paper}
                sx={{
                    maxHeight: '75vh',
                    overflow: 'auto',
                    '& .MuiTableCell-root': {
                        borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                        padding: '2px 4px',
                    },
                    '& .MuiTableCell-root:last-child': {
                        borderRight: 'none'
                    }
                }}
            >
                <Table size="small">
                    <TableHead>
                        <StyledTableRow>
                            <StyledTableCell
                                sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1001,
                                    minWidth: '180px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Cuadrilla
                            </StyledTableCell>
                            {[...Array(diasMes).keys()].map(i => {
                                const dayNum = i + 1;
                                const fecha = moment(mesSeleccionado, "YYYY-MM").date(dayNum).locale('es');
                                const diaSemana = fecha.format('dd');
                                const diaCapitalizado = diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1);
                                const esDomingo = fecha.day() === 0;
                                const esFinde = esDomingo || fecha.day() === 6;
                                const esFeriado = !!feriados[dayNum];
                                const headerSx = esFeriado
                                    ? { backgroundColor: '#c9b501 !important', color: '#827717' }
                                    : esDomingo
                                        ? { backgroundColor: '#f6882d !important', color: '#e65100' }
                                        : {};
                                return (
                                    <Tooltip key={i} title={esFeriado ? `Feriado: ${feriados[dayNum]}` : ''} arrow disableHoverListener={!esFeriado}>
                                        <StyledTableCell
                                            align="center"
                                            sx={{
                                                minWidth: '36px',
                                                width: '36px',
                                                padding: '2px 4px',
                                                lineHeight: 1,
                                                ...headerSx,
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                                                <span style={{ fontSize: '0.6rem', opacity: 0.75, fontWeight: esFinde || esFeriado ? 700 : 400 }}>
                                                    {diaCapitalizado}
                                                </span>
                                                <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                    {dayNum}
                                                </span>
                                            </div>
                                        </StyledTableCell>
                                    </Tooltip>
                                );
                            })}
                            <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Total</StyledTableCell>
                            <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Prom. día</StyledTableCell>
                            <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }} sortDirection={sortBy === 'facturacionTotal' ? sortOrder : false}>
                                <TableSortLabel
                                    active={sortBy === 'facturacionTotal'}
                                    direction={sortBy === 'facturacionTotal' ? sortOrder : 'desc'}
                                    onClick={() => handleSort('facturacionTotal')}
                                    sx={{
                                        color: 'white !important',
                                        '& .MuiTableSortLabel-icon': { color: 'white !important', opacity: 1 },
                                        '&:hover': { color: 'rgba(255,255,255,0.85) !important' },
                                        '&:hover .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.85) !important', opacity: 1 },
                                        '&.Mui-active': { color: 'white !important' },
                                        '&.Mui-active .MuiTableSortLabel-icon': { color: 'white !important', opacity: 1 },
                                    }}
                                >
                                    Facturación total
                                </TableSortLabel>
                            </StyledTableCell>
                            <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }} sortDirection={sortBy === 'promedioFacturacion' ? sortOrder : false}>
                                <TableSortLabel
                                    active={sortBy === 'promedioFacturacion'}
                                    direction={sortBy === 'promedioFacturacion' ? sortOrder : 'desc'}
                                    onClick={() => handleSort('promedioFacturacion')}
                                    sx={{
                                        color: 'white !important',
                                        '& .MuiTableSortLabel-icon': { color: 'white !important', opacity: 1 },
                                        '&:hover': { color: 'rgba(255,255,255,0.85) !important' },
                                        '&:hover .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.85) !important', opacity: 1 },
                                        '&.Mui-active': { color: 'white !important' },
                                        '&.Mui-active .MuiTableSortLabel-icon': { color: 'white !important', opacity: 1 },
                                    }}
                                >
                                    Prom. facturación
                                </TableSortLabel>
                            </StyledTableCell>
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {sortedCuadrillaIds?.length > 0 ? (
                            sortedCuadrillaIds.map((idCuadrilla) => {
                            const total = ordenesPorCuadrilla[idCuadrilla].total;
                            const facturacionTotal = ordenesPorCuadrilla[idCuadrilla].facturacionTotal ?? 0;
                            const dias = ordenesPorCuadrilla[idCuadrilla].dias;
                            const diasConProduccion = [...Array(diasMes).keys()].filter(i => dias[i + 1]).length;
                            const promedio = diasConProduccion > 0 ? (total / diasConProduccion).toFixed(2) : "-";
                            const promedioFacturacion = diasConProduccion > 0 ? formatCurrency(facturacionTotal / diasConProduccion) : "-";
                            return (
                            <StyledTableRow key={idCuadrilla} sx={{ height: '32px' }}>
                                <StyledTableCell
                                    sx={{
                                        fontWeight: 'bold',
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 1000,
                                        minWidth: '180px',
                                        backgroundColor: '#ffffff',
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.7rem',
                                    }}
                                >
                                    {ordenesPorCuadrilla[idCuadrilla].usuarioCuadrilla ? `${ordenesPorCuadrilla[idCuadrilla].usuarioCuadrilla} - ${ordenesPorCuadrilla[idCuadrilla].cuadrilla}` : ordenesPorCuadrilla[idCuadrilla].cuadrilla}
                                </StyledTableCell>
                                {[...Array(diasMes).keys()].map(i => {
                                    const dayNum = i + 1;
                                    const count = ordenesPorCuadrilla[idCuadrilla].dias[dayNum] || 0;
                                    const hasOrders = count > 0;
                                    
                                    const asistenciaCuadrilla = asistenciasMes[String(idCuadrilla)];
                                    const isAbsent = asistenciaCuadrilla?.dias_no_asistidos?.[dayNum];
                                    const esBackup = asistenciaCuadrilla?.dias_backup?.[dayNum];
                                    
                                    const fecha = moment(mesSeleccionado, "YYYY-MM").date(dayNum);
                                    const esDomingo = fecha.day() === 0;
                                    const esFeriado = !!feriados[dayNum];
                                    const esDomingoOFeriado = esDomingo || esFeriado;

                                    let cellBg = 'inherit';
                                    if (esDomingoOFeriado) {
                                        cellBg = hasOrders ? "#c8e6c9" : esDomingo ? "#ffcc80" : "#fff176";
                                    } else if (isAbsent) {
                                        cellBg = hasOrders ? 'inherit' : '#ffcdd2';
                                    } else if (esBackup) {
                                        cellBg = hasOrders ? 'inherit' : '#e3f2fd';
                                    }

                                    return (
                                        <StyledTableCell
                                            key={dayNum}
                                            align="center"
                                            sx={{
                                                backgroundColor: cellBg,
                                                minWidth: '36px',
                                                width: '36px',
                                                padding: '2px 4px',
                                                fontSize: '0.8rem',
                                            }}
                                        >
                                            {count || "-"}
                                        </StyledTableCell>
                                    );
                                })}
                                <StyledTableCell align="center">{total}</StyledTableCell>
                                <StyledTableCell align="center">{promedio}</StyledTableCell>
                                <StyledTableCell align="center">{formatCurrency(facturacionTotal)}</StyledTableCell>
                                <StyledTableCell align="center">{promedioFacturacion}</StyledTableCell>
                            </StyledTableRow>
                            );
                        })
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={35} align="center">
                                    {ordenes?.length === 0 ? (
                                        <Typography variant="h5">No se encontraron resultados</Typography>
                                    ) : (
                                        <CircularProgress />
                                    )}
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default Produccion;
